/**
 * Cloudinary URL transformation utilities for ESPN-style broadcast visuals.
 * Non-Cloudinary URLs are returned unchanged.
 */

/** Format, quality, DPR optimization - applied first for CDN caching */
const OPTIMIZE = 'f_auto,q_auto,dpr_auto';

export function isCloudinaryUrl(url: string | undefined | null): boolean {
  return typeof url === 'string' && url.includes('res.cloudinary.com');
}

/**
 * Inject transformation params into a Cloudinary URL.
 * Format: https://res.cloudinary.com/{cloud}/image/upload/{version}/{path}
 * We insert transforms before the version.
 */
function injectTransforms(rawUrl: string, transforms: string): string {
  // Match: base (upload/) + version (v123) + path
  const match = rawUrl.match(
    /^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)([^/]+)(\/.+)$/
  );
  if (!match) return rawUrl;
  const [, base, version, path] = match;
  return `${base}${transforms}/${version}${path}`;
}

/**
 * Scorebug: small circular logo for matchups, grid, scoreboard.
 * f_auto,q_auto,dpr_auto + w,h,c_fill,r_max
 */
export function getScorebugLogoUrl(rawUrl: string | undefined | null, size = 100): string {
  if (!rawUrl || !isCloudinaryUrl(rawUrl)) return rawUrl ?? '';
  const transforms = `${OPTIMIZE},w_${size},h_${size},c_fill,r_max`;
  return injectTransforms(rawUrl, transforms);
}

/**
 * Standings: grayscale watermark for background.
 * f_auto,q_auto,dpr_auto + e_grayscale,o_{opacity}
 */
export function getStandingsLogoUrl(rawUrl: string | undefined | null, opacity = 20): string {
  if (!rawUrl || !isCloudinaryUrl(rawUrl)) return rawUrl ?? '';
  const transforms = `${OPTIMIZE},e_grayscale,o_${opacity}`;
  return injectTransforms(rawUrl, transforms);
}

/**
 * Thumbnail: for cards, org picker.
 * f_auto,q_auto,dpr_auto + w,h,c_fill
 */
export function getThumbnailUrl(
  rawUrl: string | undefined | null,
  width = 200,
  height = 200
): string {
  if (!rawUrl || !isCloudinaryUrl(rawUrl)) return rawUrl ?? '';
  const transforms = `${OPTIMIZE},w_${width},h_${height},c_fill`;
  return injectTransforms(rawUrl, transforms);
}

/**
 * Width-limited optimize without crop — marketing images / mockups.
 */
export function getScaledUrl(rawUrl: string | undefined | null, width = 800): string {
  if (!rawUrl || !isCloudinaryUrl(rawUrl)) return rawUrl ?? '';
  const transforms = `${OPTIMIZE},w_${width},c_limit`;
  return injectTransforms(rawUrl, transforms);
}
